import type { RouteRecordNameGeneric, Router } from 'vue-router';
import { useSecurityStore, type ConfidentialScope } from '@/stores/security.store';
import { REVEAL_TIMEOUT_MS } from '@/composables/useTimedReveal';

/**
 * The confidential-scope guard: re-locks a shared unlock (see
 * `ConfidentialScope` in `stores/security.store.ts`) once the user leaves the
 * screens that share it, or leaves the app long enough to walk away from it.
 *
 * Why a router hook and not `onIonViewDidLeave`: the Payslips list stays alive
 * UNDER the Payslip Preview (Ionic keeps a pushed page in the stack), so the
 * list's leave hook fires on list → preview — exactly the move that must keep
 * the unlock. Only the destination route says whether the user is still in the
 * flow, and `afterEach` is where every navigation reports it: taps, back
 * gestures, hardware back, deep links from a notification.
 *
 * Sign-out needs nothing here — `security.store.reset()` drops every scope.
 */

/** Route names that together form the payslip flow — one unlock covers them all. */
export const PAYSLIP_SCOPE_ROUTES: ReadonlySet<string> = new Set<string>(['payslips', 'payslip-preview']);

const SCOPE_ROUTES: Record<ConfidentialScope, ReadonlySet<string>> = {
  payslips: PAYSLIP_SCOPE_ROUTES,
};

/**
 * Whether an unlocked `scope` survives a navigation to `toRouteName`. Pure.
 *
 * Kept only when the destination is one of the scope's own routes. A route with
 * no name, or a symbol name, is outside every scope — the safe answer is to
 * re-lock.
 */
export function shouldKeepScope(scope: ConfidentialScope, toRouteName: RouteRecordNameGeneric | null | undefined): boolean {
  if (typeof toRouteName !== 'string') return false;
  return SCOPE_ROUTES[scope]?.has(toRouteName) ?? false;
}

/** The slice of `document` the background re-lock needs — injectable for tests. */
export interface VisibilitySource {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

/**
 * Install the scope guard on the app's router. Call ONCE from `bootApp()`,
 * behind the mobile-only wall. Returns a disposer (used by tests).
 *
 *   - `router.afterEach`: a navigation that lands outside a scope's routes
 *     re-locks that scope. A navigation that FAILED (aborted, cancelled,
 *     duplicated) leaves the user where they were, so it changes nothing.
 *   - `visibilitychange`: coming back after {@link REVEAL_TIMEOUT_MS} or more in
 *     the background re-locks EVERY scope — the same rule the per-value reveals
 *     follow. Time away is measured on both clocks and the larger wins: the
 *     monotonic clock can stop while the device sleeps, the wall clock loses
 *     time when it is set backwards.
 */
export function installConfidentialScope(
  router: Pick<Router, 'afterEach'>,
  visibility: VisibilitySource | null = typeof document !== 'undefined' ? document : null,
): () => void {
  const removeAfterEach = router.afterEach((to, _from, failure) => {
    if (failure) return;
    const security = useSecurityStore();
    for (const scope of security.unlockedScopes) {
      if (!shouldKeepScope(scope, to.name)) security.lockScope(scope);
    }
  });

  if (!visibility) return removeAfterEach;

  let hiddenAt: { wall: number; mono: number } | null = null;

  const onVisibilityChange = (): void => {
    if (visibility.visibilityState === 'hidden') {
      hiddenAt = { wall: Date.now(), mono: performance.now() };
      return;
    }
    if (!hiddenAt) return;
    const away = Math.max(Date.now() - hiddenAt.wall, performance.now() - hiddenAt.mono);
    hiddenAt = null;
    if (away < REVEAL_TIMEOUT_MS) return;
    const security = useSecurityStore();
    for (const scope of security.unlockedScopes) security.lockScope(scope);
  };

  visibility.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    removeAfterEach();
    visibility.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
